import { useState, useEffect } from 'react';
import { Check, SkipForward, ArrowRight, ArrowLeft, Building2, UserPlus, Printer, Package, HandCoins } from 'lucide-react';
import { toast } from 'sonner';
import { GlassCard } from '@/components/ui/glass-card';
import { Button } from '@/components/ui/button';
import { PageTransition } from '@/components/page-transition';
import { apiFetch, getStoredUser } from '@/lib/api';

// ─── Shared form styles ───────────────────────────────────────────────────────
const inputClass = 'h-11 w-full rounded-xl border border-border bg-background px-4 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30';
const labelClass = 'text-sm font-medium text-muted-foreground mb-1.5 block';

// ─── CNPJ mask helper ─────────────────────────────────────────────────────────
function maskCnpj(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

// ─── Phone mask helper ────────────────────────────────────────────────────────
function maskPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
  return digits
    .replace(/^(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2');
}

// ─── Form Props ───────────────────────────────────────────────────────────────
interface FormProps {
  onComplete: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// 1. EstablishmentForm
// ═══════════════════════════════════════════════════════════════════════════════
function EstablishmentForm({ onComplete }: FormProps) {
  const [form, setForm] = useState({
    tradeName: '',
    legalName: '',
    cnpj: '',
    phone: '',
    address: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.tradeName || !form.legalName || !form.cnpj || !form.phone || !form.address) {
      toast.error('Preencha todos os campos obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          nome_fantasia: form.tradeName,
          razao_social: form.legalName,
          cnpj: form.cnpj,
          telefone: form.phone,
          endereco: form.address,
        }),
      });
      if (!res.ok) throw new Error('Falha ao salvar');
      toast.success('Dados do estabelecimento salvos!');
      onComplete();
    } catch {
      toast.error('Erro ao salvar dados do estabelecimento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className={labelClass}>Nome Fantasia *</label>
        <input
          type="text"
          className={inputClass}
          value={form.tradeName}
          onChange={(e) => setForm({ ...form, tradeName: e.target.value })}
          placeholder="Ex: Mercadinho do João"
          required
        />
      </div>
      <div>
        <label className={labelClass}>Razão Social *</label>
        <input
          type="text"
          className={inputClass}
          value={form.legalName}
          onChange={(e) => setForm({ ...form, legalName: e.target.value })}
          placeholder="Ex: João da Silva Comércio LTDA"
          required
        />
      </div>
      <div>
        <label className={labelClass}>CNPJ *</label>
        <input
          type="text"
          className={inputClass}
          value={form.cnpj}
          onChange={(e) => setForm({ ...form, cnpj: maskCnpj(e.target.value) })}
          placeholder="XX.XXX.XXX/XXXX-XX"
          required
        />
      </div>
      <div>
        <label className={labelClass}>Telefone *</label>
        <input
          type="text"
          className={inputClass}
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })}
          placeholder="(00) 00000-0000"
          required
        />
      </div>
      <div>
        <label className={labelClass}>Endereço *</label>
        <input
          type="text"
          className={inputClass}
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          placeholder="Rua, número, bairro, cidade - UF"
          required
        />
      </div>
      <Button type="submit" className="w-full" disabled={saving}>
        {saving ? 'Salvando...' : 'Salvar e Continuar'}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 2. AdminUserForm
// ═══════════════════════════════════════════════════════════════════════════════
function AdminUserForm({ onComplete }: FormProps) {
  const [form, setForm] = useState({
    name: '',
    login: '',
    password: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const user = getStoredUser();
    if (user) {
      setForm((prev) => ({ ...prev, name: user.name || '' }));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.login || !form.password) {
      toast.error('Preencha todos os campos obrigatórios.');
      return;
    }
    if (form.password.length < 4) {
      toast.error('A senha deve ter no mínimo 4 caracteres.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('As senhas não conferem.');
      return;
    }
    setSaving(true);
    try {
      const user = getStoredUser();
      const userId = user?.id || 'me';
      const res = await apiFetch(`/api/users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: form.name,
          login: form.login,
          password: form.password,
        }),
      });
      if (!res.ok) throw new Error('Falha ao salvar');
      toast.success('Usuário administrador atualizado!');
      onComplete();
    } catch {
      toast.error('Erro ao atualizar usuário.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-xl border border-border bg-primary/5 p-4">
        <p className="text-sm text-muted-foreground">
          Seu usuário administrador já foi criado durante o cadastro. Confirme os dados abaixo ou altere se necessário.
        </p>
      </div>
      <div>
        <label className={labelClass}>Nome *</label>
        <input
          type="text"
          className={inputClass}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Nome completo"
          required
        />
      </div>
      <div>
        <label className={labelClass}>Login *</label>
        <input
          type="text"
          className={inputClass}
          value={form.login}
          onChange={(e) => setForm({ ...form, login: e.target.value })}
          placeholder="Nome de usuário para login"
          required
        />
      </div>
      <div>
        <label className={labelClass}>Senha * (mínimo 4 caracteres)</label>
        <input
          type="password"
          className={inputClass}
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          placeholder="••••••••"
          minLength={4}
          required
        />
      </div>
      <div>
        <label className={labelClass}>Confirmar Senha *</label>
        <input
          type="password"
          className={inputClass}
          value={form.confirmPassword}
          onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
          placeholder="••••••••"
          required
        />
      </div>
      <Button type="submit" className="w-full" disabled={saving}>
        {saving ? 'Salvando...' : 'Confirmar e Continuar'}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 3. PrinterForm
// ═══════════════════════════════════════════════════════════════════════════════
function PrinterForm({ onComplete }: FormProps) {
  const [printerType, setPrinterType] = useState<'58mm' | '80mm' | 'none'>('80mm');

  const paperWidth = printerType === '58mm' ? 32 : printerType === '80mm' ? 48 : 0;

  const handleSave = () => {
    if (printerType === 'none') {
      localStorage.removeItem('fluxopdv_printer_width');
    } else {
      localStorage.setItem('fluxopdv_printer_width', String(paperWidth));
    }
    toast.success('Preferência de impressora salva!');
    onComplete();
  };

  const handleTestPrint = () => {
    const width = paperWidth || 48;
    const separator = '-'.repeat(width);
    const lines = [
      separator,
      'TESTE DE IMPRESSÃO'.padStart(Math.floor((width + 18) / 2)),
      separator,
      `Data: ${new Date().toLocaleString('pt-BR')}`,
      `Largura: ${width} colunas`,
      '',
      'Item 1..................R$ 10,00',
      'Item 2..................R$ 25,50',
      separator,
      'TOTAL...................R$ 35,50',
      separator,
      'Impressora configurada com sucesso!',
      separator,
    ];
    const content = lines.join('\n');
    const win = window.open('', '_blank', 'width=400,height=600');
    if (win) {
      win.document.write(`<pre style="font-family:monospace;font-size:12px;margin:20px;">${content}</pre>`);
      win.document.close();
      win.print();
    }
    toast.success('Teste de impressão enviado!');
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={labelClass}>Tipo de Impressora</label>
        <div className="space-y-3 mt-2">
          {([
            { value: '58mm' as const, label: 'Térmica 58mm', desc: '32 colunas - compacta' },
            { value: '80mm' as const, label: 'Térmica 80mm', desc: '48 colunas - padrão' },
            { value: 'none' as const, label: 'Não tenho impressora', desc: 'Configurar depois' },
          ]).map((option) => (
            <label
              key={option.value}
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-colors ${
                printerType === option.value
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-card hover:border-primary/30'
              }`}
            >
              <input
                type="radio"
                name="printerType"
                value={option.value}
                checked={printerType === option.value}
                onChange={() => setPrinterType(option.value)}
                className="h-4 w-4 accent-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">{option.label}</span>
                <p className="text-xs text-muted-foreground">{option.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {printerType !== 'none' && (
        <div>
          <label className={labelClass}>Largura do papel</label>
          <input
            type="text"
            className={inputClass}
            value={`${paperWidth} colunas`}
            disabled
          />
        </div>
      )}

      {printerType !== 'none' && (
        <Button type="button" variant="outline" className="w-full" onClick={handleTestPrint}>
          <Printer className="mr-2 h-4 w-4" />
          Imprimir Teste
        </Button>
      )}

      <Button type="button" className="w-full" onClick={handleSave}>
        Salvar e Continuar
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 4. ProductsForm
// ═══════════════════════════════════════════════════════════════════════════════
function ProductsForm({ onComplete }: FormProps) {
  const [form, setForm] = useState({
    barcode: '',
    name: '',
    salePrice: '',
    costPrice: '',
    unit: 'UN',
    category: '',
  });
  const [productCount, setProductCount] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCount();
  }, []);

  const fetchCount = async () => {
    try {
      const res = await apiFetch('/api/products?limit=1');
      if (res.ok) {
        const data = await res.json();
        setProductCount(data.total ?? data.products?.length ?? 0);
      }
    } catch {
      // ignore
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.salePrice) {
      toast.error('Nome e Preço de Venda são obrigatórios.');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/api/products', {
        method: 'POST',
        body: JSON.stringify({
          barcode: form.barcode || undefined,
          name: form.name,
          sale_price: parseFloat(form.salePrice.replace(',', '.')),
          cost_price: form.costPrice ? parseFloat(form.costPrice.replace(',', '.')) : undefined,
          unit: form.unit,
          category: form.category || undefined,
        }),
      });
      if (!res.ok) throw new Error('Falha ao cadastrar');
      toast.success(`Produto "${form.name}" cadastrado!`);
      setForm({ barcode: '', name: '', salePrice: '', costPrice: '', unit: 'UN', category: '' });
      setProductCount((c) => c + 1);
    } catch {
      toast.error('Erro ao cadastrar produto.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-primary/5 p-4">
        <p className="text-sm text-muted-foreground">
          Você pode cadastrar produtos manualmente ou importar de uma planilha.
        </p>
        {productCount > 0 && (
          <p className="mt-2 text-sm font-medium text-foreground">
            {productCount} produto{productCount !== 1 ? 's' : ''} já cadastrado{productCount !== 1 ? 's' : ''}.
          </p>
        )}
      </div>

      <form onSubmit={handleAdd} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Código de Barras</label>
            <input
              type="text"
              className={inputClass}
              value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              placeholder="Opcional"
            />
          </div>
          <div>
            <label className={labelClass}>Nome *</label>
            <input
              type="text"
              className={inputClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nome do produto"
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Preço de Venda *</label>
            <input
              type="text"
              className={inputClass}
              value={form.salePrice}
              onChange={(e) => setForm({ ...form, salePrice: e.target.value })}
              placeholder="0,00"
              required
            />
          </div>
          <div>
            <label className={labelClass}>Preço de Custo</label>
            <input
              type="text"
              className={inputClass}
              value={form.costPrice}
              onChange={(e) => setForm({ ...form, costPrice: e.target.value })}
              placeholder="0,00"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Unidade</label>
            <select
              className={inputClass}
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
            >
              <option value="UN">UN - Unidade</option>
              <option value="KG">KG - Quilograma</option>
              <option value="L">L - Litro</option>
              <option value="M">M - Metro</option>
              <option value="CX">CX - Caixa</option>
              <option value="PCT">PCT - Pacote</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Categoria</label>
            <input
              type="text"
              className={inputClass}
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Ex: Bebidas"
            />
          </div>
        </div>
        <Button type="submit" variant="secondary" className="w-full" disabled={saving}>
          {saving ? 'Cadastrando...' : 'Adicionar Produto'}
        </Button>
      </form>

      <Button type="button" className="w-full" onClick={onComplete}>
        {productCount > 0 ? 'Continuar' : 'Cadastrar depois no painel'}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// 5. CreditForm
// ═══════════════════════════════════════════════════════════════════════════════
function CreditForm({ onComplete }: FormProps) {
  const [form, setForm] = useState({
    creditDueDays: '30',
    maxDiscountPercent: '10',
    defaultMinStock: '5',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          dias_vencimento_fiado: parseInt(form.creditDueDays) || 30,
          percentual_max_desconto: Math.min(parseInt(form.maxDiscountPercent) || 10, 100),
          nivel_minimo_estoque_padrao: parseInt(form.defaultMinStock) || 5,
        }),
      });
      if (!res.ok) throw new Error('Falha ao salvar');
      toast.success('Configurações de crédito salvas!');
      onComplete();
    } catch {
      toast.error('Erro ao salvar configurações.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className={labelClass}>Dias para vencimento do crédito</label>
        <input
          type="number"
          className={inputClass}
          value={form.creditDueDays}
          onChange={(e) => setForm({ ...form, creditDueDays: e.target.value })}
          min={1}
          placeholder="30"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Após esse período, a dívida será considerada vencida.
        </p>
      </div>
      <div>
        <label className={labelClass}>Percentual máximo de desconto (%)</label>
        <input
          type="number"
          className={inputClass}
          value={form.maxDiscountPercent}
          onChange={(e) => setForm({ ...form, maxDiscountPercent: e.target.value })}
          min={0}
          max={100}
          placeholder="10"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Limite de desconto que operadores podem aplicar sem autorização.
        </p>
      </div>
      <div>
        <label className={labelClass}>Nível mínimo de estoque padrão</label>
        <input
          type="number"
          className={inputClass}
          value={form.defaultMinStock}
          onChange={(e) => setForm({ ...form, defaultMinStock: e.target.value })}
          min={0}
          placeholder="5"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Alerta quando o estoque de um produto ficar abaixo desse valor.
        </p>
      </div>
      <Button type="submit" className="w-full" disabled={saving}>
        {saving ? 'Salvando...' : 'Salvar e Continuar'}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main OnboardingPage Component
// ═══════════════════════════════════════════════════════════════════════════════

interface OnboardingStep {
  id: string;
  label: string;
  completed: boolean;
  skipped: boolean;
}

interface OnboardingStatus {
  complete: boolean;
  steps: OnboardingStep[];
  progress: number;
  totalSteps: number;
  completedSteps: number;
}

const stepIcons: Record<string, React.ElementType> = {
  establishment: Building2,
  admin_user: UserPlus,
  printer: Printer,
  products: Package,
  credit: HandCoins,
};

const stepDescriptions: Record<string, string> = {
  establishment: 'Configure os dados básicos do seu estabelecimento como nome, CNPJ e endereço.',
  admin_user: 'Crie o primeiro usuário administrador que terá acesso completo ao sistema.',
  printer: 'Configure a impressora térmica para emissão de cupons fiscais.',
  products: 'Cadastre seus primeiros produtos para começar a vender.',
  credit: 'Defina as regras de crédito: limite padrão e dias para vencimento.',
};

export function OnboardingPage() {
  const [status, setStatus] = useState<OnboardingStatus | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/onboarding/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data: OnboardingStatus = await response.json();
        setStatus(data);
        // Find first incomplete step
        const firstIncomplete = data.steps.findIndex((s) => !s.completed && !s.skipped);
        if (firstIncomplete >= 0) setCurrentStep(firstIncomplete);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (stepId: string) => {
    const token = localStorage.getItem('token');
    await fetch(`/api/onboarding/steps/${stepId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ completed: true }),
    });
    await fetchStatus();
    // Move to next step
    if (status && currentStep < status.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkip = async (stepId: string) => {
    const token = localStorage.getItem('token');
    await fetch(`/api/onboarding/skip/${stepId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchStatus();
    if (status && currentStep < status.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleFinish = async () => {
    const token = localStorage.getItem('token');
    await fetch('/api/onboarding/complete', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    window.location.href = '/admin/dashboard';
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <GlassCard className="animate-pulse p-8">
          <div className="h-64 w-96" />
        </GlassCard>
      </div>
    );
  }

  if (!status) return null;

  const allDone = status.steps.every((s) => s.completed || s.skipped);
  const activeStep = status.steps[currentStep];
  const StepIcon = activeStep ? stepIcons[activeStep.id] || Package : Package;

  return (
    <PageTransition>
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-2xl">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-foreground">Configuração Inicial</h1>
            <p className="mt-1 text-base text-muted-foreground">
              Vamos configurar seu estabelecimento em poucos passos
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {status.steps.map((step, index) => {
                const Icon = stepIcons[step.id] || Package;
                const isActive = index === currentStep;
                const isDone = step.completed || step.skipped;

                return (
                  <div key={step.id} className="flex flex-1 items-center">
                    <button
                      onClick={() => setCurrentStep(index)}
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 transition-all ${
                        isDone
                          ? 'border-primary bg-primary text-primary-foreground'
                          : isActive
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border bg-card text-muted-foreground'
                      }`}
                    >
                      {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                    </button>
                    {index < status.steps.length - 1 && (
                      <div
                        className={`mx-2 h-0.5 flex-1 rounded-full transition-colors ${
                          isDone ? 'bg-primary' : 'bg-border'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex justify-between">
              {status.steps.map((step) => (
                <span key={step.id} className="max-w-[80px] text-center text-xs text-muted-foreground">
                  {step.label}
                </span>
              ))}
            </div>
          </div>

          {/* Step Content */}
          {allDone ? (
            <GlassCard className="p-8 text-center">
              <div className="mb-4 flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <Check className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h2 className="mb-2 text-xl font-bold text-foreground">Tudo pronto!</h2>
              <p className="mb-6 text-muted-foreground">
                Seu estabelecimento está configurado. Você pode acessar o painel administrativo agora.
              </p>
              <Button onClick={handleFinish} className="w-full">
                Ir para o Dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </GlassCard>
          ) : activeStep ? (
            <GlassCard className="p-8">
              <div className="mb-6 flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground">{activeStep.label}</h2>
                  <p className="text-sm text-muted-foreground">
                    Etapa {currentStep + 1} de {status.totalSteps}
                  </p>
                </div>
              </div>

              <p className="mb-8 text-sm text-muted-foreground">
                {stepDescriptions[activeStep.id]}
              </p>

              {/* Step-specific form content */}
              <div className="mb-8">
                {activeStep.id === 'establishment' && <EstablishmentForm onComplete={() => handleComplete('establishment')} />}
                {activeStep.id === 'admin_user' && <AdminUserForm onComplete={() => handleComplete('admin_user')} />}
                {activeStep.id === 'printer' && <PrinterForm onComplete={() => handleComplete('printer')} />}
                {activeStep.id === 'products' && <ProductsForm onComplete={() => handleComplete('products')} />}
                {activeStep.id === 'credit' && <CreditForm onComplete={() => handleComplete('credit')} />}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                  disabled={currentStep === 0}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Anterior
                </Button>

                <Button
                  variant="ghost"
                  onClick={() => handleSkip(activeStep.id)}
                  className="text-muted-foreground"
                >
                  <SkipForward className="mr-2 h-4 w-4" />
                  Pular
                </Button>
              </div>
            </GlassCard>
          ) : null}

          {/* Skip all */}
          {!allDone && (
            <p className="mt-4 text-center text-xs text-muted-foreground">
              Você pode pular etapas e configurar depois. Um lembrete será exibido no painel.
            </p>
          )}
        </div>
      </div>
    </PageTransition>
  );
}
